import { useState } from "react";
import { Link } from "react-router-dom";
import type { Feedback } from "@/mocks/admin/feedback";

export default function FeedbackCard({ feedback }: { feedback: Feedback }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sent, setSent] = useState(!!feedback.reply);

  const ratingColor =
    feedback.rating >= 4 ? "text-emerald-700 bg-emerald-50" : feedback.rating === 3 ? "text-amber-700 bg-amber-50" : "text-rose-700 bg-rose-50";

  const statusBadge =
    feedback.status === "new"
      ? { label: "New", bg: "bg-brand-50", text: "text-brand-700", dot: "bg-brand-500" }
      : feedback.status === "flagged"
        ? { label: "Needs Attention", bg: "bg-rose-50", text: "text-rose-700", dot: "bg-rose-500" }
        : { label: "Replied", bg: "bg-ink-100", text: "text-ink-700", dot: "bg-ink-500" };

  return (
    <article className="bg-white border border-ink-100 rounded-lg p-5">
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className={`w-11 h-11 rounded-full ${feedback.color} text-white text-xs font-bold flex items-center justify-center shrink-0`}
        >
          {feedback.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-ink-900 truncate">{feedback.customer}</h3>
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusBadge.bg} ${statusBadge.text}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot}`}></span>
              {statusBadge.label}
            </span>
          </div>
          <div className="text-[11px] text-ink-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <Link
              to={`/projects/${feedback.projectId}`}
              className="font-mono font-bold text-brand-700 hover:underline cursor-pointer"
            >
              {feedback.projectId}
            </Link>
            <span>·</span>
            <span>{feedback.moveType}</span>
            <span>·</span>
            <span>{feedback.date}</span>
            <span>·</span>
            <span className="font-semibold">{feedback.source}</span>
          </div>
        </div>
        <div
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-xs ${ratingColor}`}
        >
          <i className="ri-star-fill text-[12px]"></i>
          {feedback.rating}.0
        </div>
      </div>

      {/* Stars */}
      <div className="flex items-center gap-0.5 mb-3">
        {[1, 2, 3, 4, 5].map((s) => (
          <i
            key={s}
            className={`ri-star-fill text-sm ${s <= feedback.rating ? "text-amber-500" : "text-ink-200"}`}
          ></i>
        ))}
      </div>

      {/* Title + body */}
      <h4 className="font-display text-base font-bold text-ink-900 mb-1.5">{feedback.title}</h4>
      <p className="text-sm text-ink-700 leading-relaxed">{feedback.body}</p>

      {/* Crew mentioned */}
      {feedback.crewMentioned.length > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider text-ink-400">
            Crew mentioned
          </span>
          {feedback.crewMentioned.map((c) => (
            <span
              key={c.name}
              className="inline-flex items-center gap-1.5 bg-cream-100 px-2 py-1 rounded-full"
            >
              <span
                className={`w-5 h-5 rounded-full ${c.color} text-white text-[9px] font-bold flex items-center justify-center`}
              >
                {c.initials}
              </span>
              <span className="text-[11px] font-semibold text-ink-700">{c.name}</span>
            </span>
          ))}
        </div>
      )}

      {/* Existing reply */}
      {sent && feedback.reply && (
        <div className="mt-4 pt-4 border-t border-ink-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-ink-900 text-white text-[10px] font-bold flex items-center justify-center">
              H
            </span>
            <span className="text-xs font-semibold text-ink-900">Haulmark Movers replied</span>
            <span className="text-[10px] text-ink-400">· {feedback.reply.date}</span>
          </div>
          <div className="ml-8 bg-cream-50 border border-ink-100 rounded-md p-3 text-sm text-ink-700 leading-relaxed">
            {feedback.reply.body}
          </div>
          <div className="ml-8 mt-1.5 text-[10px] text-ink-400">— {feedback.reply.author}</div>
        </div>
      )}

      {/* Reply UI */}
      {!sent && (
        <div className="mt-4 pt-4 border-t border-ink-100">
          {!replyOpen ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setReplyOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap"
              >
                <i className="ri-reply-line"></i>
                Reply
              </button>
              <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
                <i className="ri-flag-line"></i>
                Flag
              </button>
              {feedback.rating <= 3 && (
                <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 cursor-pointer whitespace-nowrap">
                  <i className="ri-error-warning-line"></i>
                  Escalate
                </button>
              )}
            </div>
          ) : (
            <div className="bg-cream-50 border border-ink-200 rounded-md p-3">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={3}
                placeholder="Write a thoughtful reply to the customer..."
                className="w-full text-sm bg-transparent outline-none resize-none placeholder:text-ink-400"
              />
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-ink-100">
                <button className="text-xs font-semibold text-ink-500 hover:text-ink-900 cursor-pointer">
                  <i className="ri-magic-line mr-1"></i>
                  Suggest reply
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setReplyOpen(false);
                      setReplyText("");
                    }}
                    className="text-xs font-semibold text-ink-500 hover:text-ink-900 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (replyText.trim()) setSent(true);
                    }}
                    className="text-xs font-semibold bg-brand-500 hover:bg-brand-600 text-white px-3 py-1.5 rounded-md cursor-pointer whitespace-nowrap"
                  >
                    Send reply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}